<script lang="ts" setup>
import { computed, h, onMounted, onUnmounted, ref } from 'vue'
import { parse } from 'yaml'
import { Checkbox } from '@/components/ui/checkbox'
import { MultiSelect } from '@/components/ui/multi-select'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Switch } from '@/components/ui/switch'
import { toast } from '~/utils/sonner'
import { startPromoterTask } from '~/utils/promoterTask'
import { planEntryState } from '~/utils/promotePlanState'
import type { PromoteDestStatus } from '~/utils/promotePlanState'
import PromoterProgressPanel from '~/components/admin/PromoterProgressPanel.vue'

type ReleaseMirror = 'r2' | 'oss'
type ReleasePlatform = 'mac' | 'win'
type ReleaseChannel = 'stable' | 'beta'

type ReleaseMirrorConfig = {
  id: ReleaseMirror
  label: string
  baseUrl: string
}

type ReleaseTarget = {
  channel: ReleaseChannel
  channelLabel: string
  platform: ReleasePlatform
  platformLabel: string
  manifestPath: string
}

type ElectronUpdateFile = {
  url: string
  sha512: string
  size: number
}

type DesktopReleaseManifest = ReleaseTarget & {
  mirror: ReleaseMirror
  mirrorLabel: string
  url: string
  ok: boolean
  version: string
  path: string
  sha512: string
  releaseDate: string
  files: ElectronUpdateFile[]
  fetchedAt: number
  error?: string
}

type MetricSortDirection = 'asc' | 'desc'
type MetricWindowUnit = 'hour' | 'day'
type MetricWindowOption = {
  key: string
  label: string
  summary: string
  amount?: number
  unit?: MetricWindowUnit
  startOfMonth?: boolean
}
type MetricSortKey =
  | 'version'
  | 'platform'
  | 'installCount'
  | 'users'
  | 'heartbeatCount'
  | 'launchCount'
  | 'usageEventCount'
  | 'trafficBytes'
  | 'lastSeenAt'
type MetricPlatform = DesktopVersionMetrics['platform'] | 'all'
type DesktopMetricRow = Omit<DesktopVersionMetrics, 'platform'> & {
  platform: MetricPlatform
  sourcePlatformCount: number
}

const RELEASE_MIRRORS: ReleaseMirrorConfig[] = [
  {
    id: 'r2',
    label: 'R2',
    baseUrl: 'https://r2.dlbun.com/panda/dl/client',
  },
  {
    id: 'oss',
    label: 'OSS',
    baseUrl: 'https://xconf2.oss-accelerate.aliyuncs.com/panda/dl/client',
  },
]

const RELEASE_TARGETS: ReleaseTarget[] = [
  {
    channel: 'stable',
    channelLabel: 'stable',
    platform: 'win',
    platformLabel: 'Windows',
    manifestPath: 'win/latest.yml',
  },
  {
    channel: 'stable',
    channelLabel: 'stable',
    platform: 'mac',
    platformLabel: 'macOS',
    manifestPath: 'mac/latest-mac.yml',
  },
  {
    channel: 'beta',
    channelLabel: 'beta',
    platform: 'win',
    platformLabel: 'Windows',
    manifestPath: 'win/beta.yml',
  },
  {
    channel: 'beta',
    channelLabel: 'beta',
    platform: 'mac',
    platformLabel: 'macOS',
    manifestPath: 'mac/beta-mac.yml',
  },
]

const dayjs = useDayjs()
const $utils = useUtils()
const nativeDateTimeRangeFormat = 'YYYY-MM-DDTHH:mm'
const metricWindowOptions: MetricWindowOption[] = [
  { key: '24h', label: '24h', summary: '最近 24 小时', amount: 24, unit: 'hour' },
  { key: '48h', label: '48h', summary: '最近 48 小时', amount: 48, unit: 'hour' },
  { key: '7d', label: '7 天', summary: '最近 7 天', amount: 7, unit: 'day' },
  { key: '15d', label: '15 天', summary: '最近 15 天', amount: 15, unit: 'day' },
  { key: 'month', label: '本月', summary: '本月', startOfMonth: true },
  { key: '30d', label: '30 天', summary: '最近 30 天', amount: 30, unit: 'day' },
  { key: '90d', label: '90 天', summary: '最近 90 天', amount: 90, unit: 'day' },
]
const selectedMetricWindowKey = ref('24h')
const beijingNow = () => dayjs().tz('Asia/Shanghai')
const formatBeijingForInput = (date: ReturnType<typeof dayjs>) =>
  dayjs(date).tz(undefined, true).format(nativeDateTimeRangeFormat)
const customMetricRange = ref<[string, string]>([
  formatBeijingForInput(beijingNow().subtract(24, 'hour')),
  formatBeijingForInput(beijingNow()),
])
const mergePlatforms = ref(true)
const metricSort = ref<{ key: MetricSortKey, direction: MetricSortDirection }>({
  key: 'version',
  direction: 'desc',
})
const metricsLoading = ref(false)
const metricsError = ref('')
const manifestLoading = ref(false)
const manifestError = ref('')
const downloadVersionLoading = ref(false)
const downloadVersionSaving = ref(false)
const stableVersion = ref('')
const betaVersion = ref('')
const releaseManifests = ref<DesktopReleaseManifest[]>([])
const versionMetrics = ref<DesktopVersionMetrics[]>([])

const metricRows = computed(() => sortMetricRows(metricDisplayRows.value))
const selectedMetricWindow = computed(() =>
  metricWindowOptions.find((option) => option.key === selectedMetricWindowKey.value) || metricWindowOptions[0],
)
const isCustomMetricWindow = computed(() => selectedMetricWindowKey.value === 'custom')
const metricWindowSummary = computed(() => {
  if (!isCustomMetricWindow.value) return selectedMetricWindow.value.summary
  return `自定义范围 ${formatMetricInputTime(customMetricRange.value[0])} ~ ${formatMetricInputTime(customMetricRange.value[1])}`
})

const metricDisplayRows = computed<DesktopMetricRow[]>(() => {
  if (!mergePlatforms.value) {
    return versionMetrics.value.map((row) => ({
      ...row,
      sourcePlatformCount: 1,
    }))
  }

  const rowsByVersion = new Map<string, DesktopMetricRow>()
  for (const row of versionMetrics.value) {
    const existing = rowsByVersion.get(row.version)
    if (existing) {
      existing.installCount += row.installCount
      existing.activeUsers += row.activeUsers
      existing.heartbeatCount += row.heartbeatCount
      existing.launchCount += row.launchCount
      existing.usageEventCount += row.usageEventCount
      existing.trafficBytes += row.trafficBytes
      existing.trafficUpBytes += row.trafficUpBytes
      existing.trafficDownBytes += row.trafficDownBytes
      existing.eventDeviceCount += row.eventDeviceCount
      existing.eventUserCount += row.eventUserCount
      existing.lastHeartbeatAt = Math.max(existing.lastHeartbeatAt, row.lastHeartbeatAt)
      existing.lastEventAt = Math.max(existing.lastEventAt, row.lastEventAt)
      existing.lastSeenAt = Math.max(existing.lastSeenAt, row.lastSeenAt)
      existing.sourcePlatformCount += 1
      continue
    }

    rowsByVersion.set(row.version, {
      ...row,
      platform: 'all',
      sourcePlatformCount: 1,
    })
  }

  return [...rowsByVersion.values()]
})

const successfulManifests = computed(() => releaseManifests.value.filter((row) => row.ok))

const versionCandidates = computed(() => {
  const versions = new Set<string>()
  for (const row of successfulManifests.value) {
    if (row.version) versions.add(row.version)
  }
  for (const row of versionMetrics.value) {
    if (row.version) versions.add(row.version)
  }
  return [...versions].sort((a, b) => b.localeCompare(a, 'en', { numeric: true, sensitivity: 'base' }))
})

const versionOptions = computed(() => versionCandidates.value.map((version) => ({ label: version, value: version })))

// MultiSelect is array-based; these two fields hold a single free-form version.
const stableVersionModel = computed({
  get: () => (stableVersion.value ? [stableVersion.value] : []),
  set: (value) => {
    stableVersion.value = value?.[0] || ''
  },
})

const betaVersionModel = computed({
  get: () => (betaVersion.value ? [betaVersion.value] : []),
  set: (value) => {
    betaVersion.value = value?.[0] || ''
  },
})

const currentStableManifest = computed(() =>
  successfulManifests.value.find((row) => row.mirror === 'r2' && row.channel === 'stable')
  || successfulManifests.value.find((row) => row.channel === 'stable')
  || null,
)

const currentBetaManifest = computed(() =>
  successfulManifests.value.find((row) => row.mirror === 'r2' && row.channel === 'beta')
  || successfulManifests.value.find((row) => row.channel === 'beta')
  || null,
)

async function refresh() {
  await Promise.all([
    refreshDownloadVersions(),
    refreshReleaseManifests(),
    refreshMetrics(),
    loadVersionNotesHistory(),
  ])
}

async function refreshDownloadVersions(quiet = false) {
  downloadVersionLoading.value = true
  try {
    const data = await useAdmin.getCurrentRelease()
    stableVersion.value = String(data?.stable || '').trim()
    betaVersion.value = normalizeConfiguredBeta(data?.beta)
  } catch (error) {
    if (quiet) return
    toast({
      title: '加载教程下载版本失败',
      description: errorText(error),
      variant: 'destructive',
    })
  } finally {
    downloadVersionLoading.value = false
  }
}

async function refreshReleaseManifests(quiet = false) {
  if (manifestLoading.value) return
  manifestLoading.value = true
  if (!quiet) manifestError.value = ''
  try {
    const tasks = RELEASE_MIRRORS.flatMap((mirror) =>
      RELEASE_TARGETS.map((target) => fetchReleaseManifest(mirror, target)),
    )
    const rows = await Promise.all(tasks)
    if (quiet && !rows.some((row) => row.ok) && releaseManifests.value.some((row) => row.ok)) {
      // 后台轮询遇到全局失败:保留旧数据,不闪错,下次再试。
      return
    }
    releaseManifests.value = rows
    manifestsUpdatedAt.value = Date.now()
    noteNewManifestVersions()
    const failed = rows.filter((row) => !row.ok)
    if (!quiet) {
      manifestError.value = failed.length
        ? `${failed.length} 个 release yml 读取失败，详情见表格。`
        : ''
    } else if (!failed.length) {
      manifestError.value = ''
    }
  } finally {
    manifestLoading.value = false
  }
}

// Manifest 自刷新:产物同步/发布/切换/回滚后不再需要手动点刷新。
// 页面可见时每 30s 静默拉一次 yml + 已保存版本;写入操作后额外延迟
// 复查一次,抹平 CDN 传播延迟。Axiom 统计贵且与新版本发现无关,
// 只在进入页面和手动刷新时拉。
const MANIFEST_POLL_MS = 30_000
const MANIFEST_RECHECK_MS = 10_000
const autoRefreshEnabled = ref(true)
const manifestsUpdatedAt = ref(0)
const manifestsUpdatedLabel = computed(() => {
  const stamp = manifestsUpdatedAt.value ? dayjs(manifestsUpdatedAt.value).format('HH:mm:ss') : '尚未更新'
  return `${autoRefreshEnabled.value ? '自动刷新开' : '自动刷新已暂停'} · 更新于 ${stamp}`
})
let manifestPollTimer: ReturnType<typeof setInterval> | null = null
let manifestRecheckTimer: ReturnType<typeof setTimeout> | null = null
const seenManifestVersions = new Set<string>()
let manifestVersionsSeeded = false

function noteNewManifestVersions() {
  const fresh: string[] = []
  for (const row of successfulManifests.value) {
    if (row.version && !seenManifestVersions.has(row.version)) {
      seenManifestVersions.add(row.version)
      fresh.push(row.version)
    }
  }
  if (!manifestVersionsSeeded) {
    manifestVersionsSeeded = true
    return
  }
  if (!fresh.length) return
  const shown = fresh.slice(0, 3).join('、')
  toast({
    title: `发现新版本 ${shown}${fresh.length > 3 ? ` 等 ${fresh.length} 个` : ''}`,
    description: '已加入教程页下载版本候选',
  })
}

function scheduleManifestRecheck() {
  if (manifestRecheckTimer) clearTimeout(manifestRecheckTimer)
  manifestRecheckTimer = setTimeout(() => {
    manifestRecheckTimer = null
    refreshReleaseManifests(true)
  }, MANIFEST_RECHECK_MS)
}

async function manifestPollTick() {
  if (!autoRefreshEnabled.value || document.hidden) return
  if (manifestLoading.value || downloadVersionLoading.value) return
  await Promise.all([refreshReleaseManifests(true), refreshDownloadVersions(true)])
}

async function refreshMetrics() {
  metricsLoading.value = true
  metricsError.value = ''
  try {
    const { startTime, endTime } = metricQueryRange()
    versionMetrics.value = await useAxiom().getDesktopVersionMetrics({
      startTime,
      endTime,
      limit: 1000,
    })
  } catch (error) {
    metricsError.value = errorText(error)
    toast({
      title: '加载 V2Panda 指标失败',
      description: metricsError.value,
      variant: 'destructive',
    })
  } finally {
    metricsLoading.value = false
  }
}

async function fetchReleaseManifest(
  mirror: ReleaseMirrorConfig,
  target: ReleaseTarget,
): Promise<DesktopReleaseManifest> {
  const url = withCacheBust(`${mirror.baseUrl}/${target.manifestPath}`)
  try {
    const response = await fetch(url, { cache: 'no-store' })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    const text = await response.text()
    const doc = parse(text) as any
    const files = normalizeFiles(doc?.files)
    const path = String(doc?.path || files[0]?.url || '').trim()
    const version = String(doc?.version || '').trim()
    if (!version) {
      throw new Error('missing version field')
    }
    return {
      ...target,
      mirror: mirror.id,
      mirrorLabel: mirror.label,
      url,
      ok: true,
      version,
      path,
      sha512: String(doc?.sha512 || files.find((file) => file.url === path)?.sha512 || files[0]?.sha512 || '').trim(),
      releaseDate: String(doc?.releaseDate || '').trim(),
      files,
      fetchedAt: Date.now(),
    }
  } catch (error) {
    return {
      ...target,
      mirror: mirror.id,
      mirrorLabel: mirror.label,
      url,
      ok: false,
      version: '',
      path: '',
      sha512: '',
      releaseDate: '',
      files: [],
      fetchedAt: Date.now(),
      error: errorText(error),
    }
  }
}

function withCacheBust(url: string): string {
  const separator = url.includes('?') ? '&' : '?'
  return `${url}${separator}v=${Date.now()}`
}

function resolveManifestFileUrl(row: DesktopReleaseManifest, fileUrl: string) {
  if (!fileUrl) return ''
  const manifestUrl = row.url.split('?')[0]
  const manifestDir = manifestUrl.slice(0, manifestUrl.lastIndexOf('/') + 1)
  return new URL(fileUrl, manifestDir).href
}

function releaseDownloadUrl(row: DesktopReleaseManifest) {
  if (!row.ok || !row.path) return ''
  return resolveManifestFileUrl(row, row.path)
}

// yml files[] 展示。electron-builder 合并渠道时会写入重复条目,按 url 去重;
// mac 只显示 dmg(zip 是更新器内部用的,手动发客户都用 dmg),没有 dmg 的
// 旧 yml 退回显示全部。
function releaseFileLinks(row: DesktopReleaseManifest) {
  if (!row.ok) return []
  const source = row.files.length
    ? row.files
    : row.path
      ? [{ url: row.path, sha512: row.sha512, size: 0 }]
      : []
  const seen = new Set<string>()
  const files = source.filter((file) => {
    if (seen.has(file.url)) return false
    seen.add(file.url)
    return true
  })
  const dmgs = files.filter((file) => file.url.endsWith('.dmg'))
  const shown = row.platform === 'mac' && dmgs.length ? dmgs : files
  return shown.map((file) => ({
    name: file.url,
    href: resolveManifestFileUrl(row, file.url),
  }))
}

function normalizeFiles(value: any): ElectronUpdateFile[] {
  if (!Array.isArray(value)) return []
  return value
    .map((file) => ({
      url: String(file?.url || '').trim(),
      sha512: String(file?.sha512 || '').trim(),
      size: Number(file?.size || 0),
    }))
    .filter((file) => file.url)
}

async function setMetricWindow(key: string) {
  if (selectedMetricWindowKey.value === key && key !== 'custom') return
  if (key === 'custom' && selectedMetricWindowKey.value !== 'custom') {
    seedCustomMetricRange()
  }
  selectedMetricWindowKey.value = key
  await refreshMetrics()
}

async function applyCustomMetricRange() {
  if (!isCustomMetricWindow.value) {
    selectedMetricWindowKey.value = 'custom'
  }
  await refreshMetrics()
}

function seedCustomMetricRange() {
  const { startTime, endTime } = presetMetricQueryRange(selectedMetricWindow.value)
  customMetricRange.value = [
    formatBeijingForInput(dayjs(startTime).tz('Asia/Shanghai')),
    formatBeijingForInput(dayjs(endTime).tz('Asia/Shanghai')),
  ]
}

function metricQueryRange() {
  if (isCustomMetricWindow.value) {
    const start = parseCustomMetricTime(customMetricRange.value[0], '开始时间')
    const end = parseCustomMetricTime(customMetricRange.value[1], '结束时间')
    if (!end.isAfter(start)) {
      throw new Error('自定义范围结束时间必须晚于开始时间')
    }
    return {
      startTime: start.toISOString(),
      endTime: end.toISOString(),
    }
  }
  return presetMetricQueryRange(selectedMetricWindow.value)
}

function presetMetricQueryRange(option: MetricWindowOption) {
  const endTime = dayjs()
  if (option.startOfMonth) {
    return {
      startTime: beijingNow().startOf('month').toISOString(),
      endTime: endTime.toISOString(),
    }
  }
  if (!option.amount || !option.unit) {
    throw new Error(`未知 Axiom 时间范围: ${option.key}`)
  }
  return {
    startTime: endTime.subtract(option.amount, option.unit).toISOString(),
    endTime: endTime.toISOString(),
  }
}

function parseCustomMetricTime(value: string, label: string) {
  const text = String(value || '').trim()
  if (!text) throw new Error(`自定义范围${label}不能为空`)
  const parsed = dayjs(`${text}+08:00`)
  if (!parsed.isValid()) throw new Error(`自定义范围${label}无效`)
  return parsed
}

function formatMetricInputTime(value: string) {
  const parsed = dayjs(`${value}+08:00`)
  return parsed.isValid() ? parsed.tz('Asia/Shanghai').format('MM-DD HH:mm') : '-'
}

function toggleMetricSort(key: MetricSortKey) {
  if (metricSort.value.key === key) {
    metricSort.value.direction = metricSort.value.direction === 'asc' ? 'desc' : 'asc'
    return
  }

  metricSort.value = {
    key,
    direction: key === 'version' || key === 'platform' ? 'asc' : 'desc',
  }
}

function sortMetricRows(rows: DesktopMetricRow[]) {
  const { key, direction } = metricSort.value
  const directionMultiplier = direction === 'asc' ? 1 : -1
  return [...rows].sort((a, b) => {
    const aValue = metricSortValue(a, key)
    const bValue = metricSortValue(b, key)
    let result: number
    if (typeof aValue === 'string' || typeof bValue === 'string') {
      result = String(aValue).localeCompare(String(bValue), 'en', { numeric: true, sensitivity: 'base' })
    } else {
      result = aValue - bValue
    }
    return result * directionMultiplier || b.installCount - a.installCount || b.trafficBytes - a.trafficBytes
  })
}

function metricSortValue(row: DesktopMetricRow, key: MetricSortKey): string | number {
  if (key === 'users') return metricUserCount(row)
  if (key === 'platform') return metricPlatformLabel(row.platform)
  return row[key]
}

function metricUserCount(row: DesktopVersionMetrics | DesktopMetricRow) {
  return row.activeUsers || row.eventUserCount
}

// ---------------------------------------------------------------------------
// 用户明细 sheet — per-user breakdown of one version row, same Axiom window.
// ---------------------------------------------------------------------------
const userSheetOpen = ref(false)
const userSheetRow = ref<DesktopMetricRow | null>(null)
const userSheetRows = ref<DesktopVersionUserMetrics[]>([])
const userSheetLoading = ref(false)
const userSheetError = ref('')

const userSheetTitle = computed(() => {
  const row = userSheetRow.value
  if (!row) return '用户明细'
  return `${row.version} · ${metricPlatformLabel(row.platform)}`
})

async function openVersionUsers(row: DesktopMetricRow) {
  userSheetRow.value = row
  userSheetOpen.value = true
  userSheetRows.value = []
  userSheetError.value = ''
  userSheetLoading.value = true
  try {
    const { startTime, endTime } = metricQueryRange()
    userSheetRows.value = await useAxiom().getDesktopVersionUsers({
      version: row.version,
      platform: row.platform,
      startTime,
      endTime,
      limit: 500,
    })
  } catch (error) {
    userSheetError.value = errorText(error)
  } finally {
    userSheetLoading.value = false
  }
}

function metricSortIcon(key: MetricSortKey) {
  if (metricSort.value.key !== key) return 'material-symbols:unfold-more'
  return metricSort.value.direction === 'asc' ? 'material-symbols:arrow-upward' : 'material-symbols:arrow-downward'
}

async function saveDownloadVersions() {
  const stable = stableVersion.value.trim()
  const beta = betaVersion.value.trim()
  if (!stable) {
    toast({
      title: 'stable 版本不能为空',
      variant: 'destructive',
    })
    return
  }

  const confirmed = await useDialog().confirm({
    title: '设置教程页下载版本',
    body: `Stable: ${stable}\nBeta: ${beta || '无'}`,
    confirmButtonText: '保存',
    cancelButtonText: '取消',
    confirmButtonVariant: 'warning',
  })
  if (confirmed !== 'confirm') return

  downloadVersionSaving.value = true
  try {
    const ret = await useAdmin.setDLClientVersion({ stable, beta: beta || '-' }) as {
      stable?: string
      beta?: string
    }
    stableVersion.value = String(ret?.stable || stable).trim()
    betaVersion.value = normalizeConfiguredBeta(ret?.beta || beta)
    toast({
      title: '教程页下载版本已更新',
      description: `Stable ${stableVersion.value} / Beta ${betaVersion.value || '-'}`,
    })
  } catch (error) {
    toast({
      title: '设置教程页下载版本失败',
      description: errorText(error),
      variant: 'destructive',
    })
  } finally {
    downloadVersionSaving.value = false
  }
}

function setDraftVersion(target: 'stable' | 'beta', version: string) {
  if (target === 'stable') {
    stableVersion.value = version
    return
  }
  betaVersion.value = version
}

function normalizeConfiguredBeta(value?: string) {
  const beta = String(value || '').trim()
  return beta === '-' ? '' : beta
}

function errorText(error: any) {
  return error?.data?.message || error?.data?.error || error?.message || String(error)
}

function formatMetricNumber(value?: number | null) {
  if (value === null || value === undefined) return '-'
  return Math.round(Number(value) || 0).toLocaleString('en-US')
}

function formatBytes(bytes?: number | null) {
  const value = Number(bytes || 0)
  if (!value) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  let n = value
  let unit = 0
  while (n >= 1024 && unit < units.length - 1) {
    n /= 1024
    unit += 1
  }
  return `${n.toFixed(unit === 0 ? 0 : 2)} ${units[unit]}`
}

function formatMetricTime(epochMs?: number | null) {
  if (!epochMs) return '-'
  return dayjs(epochMs).format('MM-DD HH:mm')
}

function formatReleaseTime(value?: string) {
  if (!value) return '-'
  const date = dayjs(value)
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm') : value
}

function shortHash(hash?: string) {
  const value = String(hash || '')
  if (!value) return '-'
  return value.length > 6 ? `${value.slice(0, 3)}...${value.slice(-3)}` : value
}

function platformLabel(platform: DesktopVersionMetrics['platform']) {
  return platform === 'darwin' ? 'macOS' : 'Windows'
}

function metricPlatformLabel(platform: MetricPlatform) {
  if (platform === 'all') return '全部平台'
  return platformLabel(platform)
}

function trafficMetricTitle(metrics?: DesktopVersionMetrics | DesktopMetricRow | DesktopVersionUserMetrics | null) {
  if (!metrics) return '-'
  return `${formatBytes(metrics.trafficUpBytes)} up / ${formatBytes(metrics.trafficDownBytes)} down`
}

// ---------------------------------------------------------------------------
// CI 构建提升:build bucket → pandadl,由 v2panda-promoter worker 执行,
// 浏览器只负责编排;详见 cloudflare-workers/v2panda-promoter。
// ---------------------------------------------------------------------------
const PROMOTER_BASE = 'https://promote.dlbun.com'

type PromotePlanEntry = {
  source: string
  size: number
  kind: 'binary' | 'metadata' | 'ignored'
  dests: string[]
  destStatus: PromoteDestStatus[]
  meta?: {
    buildVersion: string
    liveVersion: string | null
    identical: boolean
  }
}
type PromoteChannel = { channel: string, mac: boolean, win: boolean }
type PromoteCopyState = 'copying' | 'done' | 'error'

const promoteBuilds = ref<string[]>([])
const promoteBuildsLoading = ref(false)
const promoteSelectedBuild = ref('')
const promotePlan = ref<PromotePlanEntry[]>([])
const promotePlanBuild = ref('')
const promoteChannels = ref<PromoteChannel[]>([])
const promotePlanLoading = ref(false)
const promoteForce = ref(false)
const promoteCopying = ref(false)
const promoteCopyState = ref<Record<string, PromoteCopyState>>({})
const promotePublishing = ref('')
const promoteError = ref('')
const promoteNotesEditor = ref<{ save: () => Promise<boolean>, refresh: () => Promise<void> } | null>(null)
const editingNotesVersion = ref('')
const notesSheetOpen = ref(false)
const versionNotesHistory = ref<Array<{ version: string, changelog: string, changelog_en: string, publishedAt: string | null }>>([])
const versionNotesHistoryLoading = ref(false)
const versionNotesHistoryError = ref('')

async function loadVersionNotesHistory() {
  versionNotesHistoryLoading.value = true
  versionNotesHistoryError.value = ''
  try {
    versionNotesHistory.value = await useAPI().get('admin/desktop-release-notes') as typeof versionNotesHistory.value
    await promoteNotesEditor.value?.refresh()
  } catch (error) {
    versionNotesHistoryError.value = errorText(error)
  } finally {
    versionNotesHistoryLoading.value = false
  }
}

function editReleaseNotes(version: string) {
  editingNotesVersion.value = version
  notesSheetOpen.value = true
}

const promoteHeaders = () => ({
  'x-panda-authorization': useVisitorSession().jwt_token || readCookieValue('jwt_token', '') || '',
})

const promoteBinaries = computed(() => promotePlan.value.filter((row) => row.kind === 'binary'))
const promoteMetadata = computed(() => promotePlan.value.filter((row) => row.kind === 'metadata'))
const promoteNotesVersion = computed(() => {
  const versions = [...new Set(promoteMetadata.value.map(row => row.meta?.buildVersion).filter(Boolean))]
  return versions.length === 1 ? versions[0]! : ''
})
// Rows the upload button copies: missing in R2, or in R2 but not yet in OSS.
const promotePending = computed(() =>
  promoteBinaries.value.filter((row) => planEntryState(row.destStatus) !== 'synced'),
)

type PromotePlatform = 'mac' | 'win' | 'both'

function promotePlatformOf(row: PromotePlanEntry): 'mac' | 'win' {
  return row.dests[0]?.includes('/win/') ? 'win' : 'mac'
}

function promoteReadyFor(platform: PromotePlatform) {
  const rows = promoteBinaries.value.filter(
    (row) => platform === 'both' || promotePlatformOf(row) === platform,
  )
  return rows.length > 0 && rows.every((row) => row.destStatus.every((dest) => dest.sizeMatches))
}

const promoteReady = computed(() => promoteReadyFor('both'))
const promoteReadyMac = computed(() => promoteReadyFor('mac'))
const promoteReadyWin = computed(() => promoteReadyFor('win'))

async function refreshPromoteBuilds() {
  promoteBuildsLoading.value = true
  promoteError.value = ''
  try {
    const data = await $fetch<{ builds: string[] }>(`${PROMOTER_BASE}/builds`, {
      headers: promoteHeaders(),
    })
    promoteBuilds.value = data.builds || []
    if (!promoteSelectedBuild.value && promoteBuilds.value.length) {
      promoteSelectedBuild.value = promoteBuilds.value[0]
    }
  } catch (error) {
    promoteError.value = errorText(error)
  } finally {
    promoteBuildsLoading.value = false
  }
}

async function loadPromotePlan() {
  if (!promoteSelectedBuild.value) return
  promotePlanLoading.value = true
  promoteError.value = ''
  promoteCopyState.value = {}
  try {
    const data = await $fetch<{ entries: PromotePlanEntry[], channels: PromoteChannel[] }>(
      `${PROMOTER_BASE}/builds/${encodeURIComponent(promoteSelectedBuild.value)}/plan`,
      { headers: promoteHeaders() },
    )
    promotePlan.value = data.entries || []
    promoteChannels.value = data.channels || []
    promotePlanBuild.value = promoteSelectedBuild.value
  } catch (error) {
    promotePlan.value = []
    promoteChannels.value = []
    promotePlanBuild.value = ''
    promoteError.value = errorText(error)
  } finally {
    promotePlanLoading.value = false
  }
}

const PROMOTE_COPY_CONCURRENCY = 4

async function runPromoteCopy() {
  const targets = promoteForce.value ? promoteBinaries.value : promotePending.value
  if (!targets.length || promoteCopying.value) return
  promoteCopying.value = true
  promoteError.value = ''
  const task = startPromoterTask(`上传产物 · ${promotePlanBuild.value}`)
  const queue = [...targets]
  const failures: string[] = []
  const copyWorker = async () => {
    for (;;) {
      const row = queue.shift()
      if (!row) return
      promoteCopyState.value = { ...promoteCopyState.value, [row.source]: 'copying' }
      task.step(`copy:${row.source}`, `上传 R2 + OSS · ${row.source}`)
      try {
        await $fetch(`${PROMOTER_BASE}/copy`, {
          method: 'POST',
          headers: promoteHeaders(),
          body: { build: promotePlanBuild.value, source: row.source },
        })
        promoteCopyState.value = { ...promoteCopyState.value, [row.source]: 'done' }
        task.endStep(`copy:${row.source}`, true)
      } catch (error) {
        promoteCopyState.value = { ...promoteCopyState.value, [row.source]: 'error' }
        failures.push(`${row.source}: ${errorText(error)}`)
        task.endStep(`copy:${row.source}`, false, `上传失败 · ${row.source}`)
      }
    }
  }
  await Promise.all(
    Array.from(
      { length: Math.min(PROMOTE_COPY_CONCURRENCY, targets.length) },
      () => copyWorker(),
    ),
  )
  if (failures.length) {
    promoteError.value = `${failures.length} 个文件上传失败:${failures.join('; ')}`
    task.fail(`上传产物部分失败:\n${failures.join('\n')}`)
  } else {
    task.succeed(`${targets.length} 个文件已上传到 R2 + OSS`)
  }
  promoteCopying.value = false
  await loadPromotePlan()
  scheduleManifestRecheck()
}

const PROMOTE_PLATFORM_LABELS: Record<PromotePlatform, string> = {
  both: 'macOS + Windows',
  mac: '仅 macOS',
  win: '仅 Windows',
}

const promotePublishMirror = ref<PromoteMirror>('both')

async function publishPromoteChannel(channel: string, platform: PromotePlatform) {
  if (promotePublishing.value) return
  const build = promotePlanBuild.value
  const publishKey = `${channel}:${platform}`
  const mirror = promotePublishMirror.value
  const confirmed = await useDialog().confirm({
    title: `发布 ${channel} 渠道更新`,
    body: `Build: ${promotePlanBuild.value}\n平台: ${PROMOTE_PLATFORM_LABELS[platform]}\n镜像: ${PROMOTE_MIRROR_LABELS[mirror]}\n发布渠道 yml 后,${channel} 渠道对应平台的客户端会立即收到这次更新。`,
    confirmButtonText: '发布',
    cancelButtonText: '取消',
    confirmButtonVariant: 'warning',
  })
  if (confirmed !== 'confirm' || build !== promotePlanBuild.value || promotePublishing.value) return
  promotePublishing.value = publishKey
  promoteError.value = ''
  const task = startPromoterTask(`发布 ${channel} · ${PROMOTE_PLATFORM_LABELS[platform]} · ${PROMOTE_MIRROR_LABELS[mirror]}`)
  try {
    task.step('notes', '保存更新说明')
    if (!promoteNotesEditor.value || !await promoteNotesEditor.value.save()) {
      promoteError.value = '请先成功加载并保存此版本的更新说明。'
      task.fail(promoteError.value)
      return
    }
    task.endStep('notes', true)
    const ret = await task.request<{
      published: string[]
      oss?: { published: string[], errors: string[] }
    }>(`${PROMOTER_BASE}/publish`, promoteHeaders(), { build, channel, platform, mirror })
    const ossErrors = ret?.oss?.errors || []
    if (ossErrors.length) {
      promoteError.value = mirror === 'oss'
        ? `OSS 发布失败,请重试:${ossErrors.join('; ')}`
        : `R2 已发布,但 OSS 同步失败,请重试发布:${ossErrors.join('; ')}`
      task.fail(promoteError.value)
    } else {
      const targets = [...(ret?.published || []), ...(ret?.oss?.published || [])]
      task.succeed(`已发布 ${channel} 渠道 (${PROMOTE_PLATFORM_LABELS[platform]})\n${PROMOTE_MIRROR_LABELS[mirror]}:${[...new Set(targets)].join('、')}`)
    }
    await Promise.all([refreshReleaseManifests(), loadPromotePlan()])
    scheduleManifestRecheck()
  } catch (error) {
    const missing = (error as any)?.data?.missing
    promoteError.value = Array.isArray(missing) && missing.length
      ? `${errorText(error)}。缺失:${missing.join('、')}`
      : errorText(error)
    task.fail(`发布 ${channel} 失败:${promoteError.value}`)
  } finally {
    promotePublishing.value = ''
  }
}

function promoteEntryState(row: PromotePlanEntry): ReturnType<typeof planEntryState> | PromoteCopyState {
  const state = promoteCopyState.value[row.source]
  if (state) return state
  return planEntryState(row.destStatus)
}

const PROMOTE_STATE_LABELS: Record<string, string> = {
  synced: '已同步',
  pending: '待上传',
  'oss-pending': '待上传 OSS',
  copying: '上传中',
  done: '已上传',
  error: '失败',
}

// 版本切换:R2/OSS yml 表格每行的「切换」按钮。候选 = 归档快照 + 携带该
// 渠道 yml 的近期 CI 构建;应用时归档条目走 /rollback、CI 构建走 /publish,
// 两者都按所选镜像校验二进制、自动归档旧版,再写入所选镜像。
type PromoteMirror = 'r2' | 'oss' | 'both'

const PROMOTE_MIRROR_LABELS: Record<PromoteMirror, string> = {
  both: 'R2 + OSS',
  r2: '仅 R2',
  oss: '仅 OSS',
}

type YmlSwitchTarget = {
  file: string
  channel: ReleaseChannel
  channelLabel: string
  platform: ReleasePlatform
  platformLabel: string
  mirror: ReleaseMirror
  mirrorLabel: string
}
type YmlSwitchCandidate = {
  id: string
  source: 'archive' | 'build'
  version: string
  label: string
  key?: string
  build?: string
}

// 每行一份独立状态,多行可以同时展开、同时切换。
type YmlSwitchState = {
  target: YmlSwitchTarget
  candidates: YmlSwitchCandidate[]
  loading: boolean
  selectedId: string
  applying: boolean
  // 默认只写打开面板那一行所属的镜像;勾选后才同写 R2 + OSS
  bothMirrors: boolean
  error: string
}

const switchStates = ref<Record<string, YmlSwitchState>>({})

function switchRowKey(row: DesktopReleaseManifest) {
  return `${row.mirror}:${ymlFileOf(row)}`
}

function switchStateOf(row: DesktopReleaseManifest): YmlSwitchState | null {
  return switchStates.value[switchRowKey(row)] || null
}

function switchEffectiveMirror(state: YmlSwitchState): PromoteMirror {
  return state.bothMirrors ? 'both' : state.target.mirror
}

function ymlFileOf(row: DesktopReleaseManifest): string {
  return row.manifestPath.split('/').pop() || ''
}

// worker 侧渠道名:latest/beta/alpha(页面的 stable = latest)
function workerChannelOf(file: string): string {
  return file.replace(/(-mac)?\.yml$/, '')
}

async function openYmlSwitcher(row: DesktopReleaseManifest) {
  const file = ymlFileOf(row)
  const key = switchRowKey(row)
  const state: YmlSwitchState = {
    target: {
      file,
      channel: row.channel,
      channelLabel: row.channelLabel,
      platform: row.platform,
      platformLabel: row.platformLabel,
      mirror: row.mirror,
      mirrorLabel: row.mirrorLabel,
    },
    candidates: [],
    loading: true,
    selectedId: '',
    applying: false,
    bothMirrors: false,
    error: '',
  }
  switchStates.value = { ...switchStates.value, [key]: state }
  // Mutations must go through the reactive proxy, not the raw literal above.
  const live = switchStates.value[key]!
  try {
    const data = await $fetch<{
      archive: Array<{ key: string, version: string, archivedAt: string }>
      builds: Array<{ build: string, version: string }>
    }>(`${PROMOTER_BASE}/yml-candidates`, {
      params: { file },
      headers: promoteHeaders(),
    })
    const candidates: YmlSwitchCandidate[] = []
    for (const entry of data.builds || []) {
      candidates.push({
        id: `build:${entry.build}`,
        source: 'build',
        version: entry.version,
        label: `CI ${entry.version} (${entry.build})`,
        build: entry.build,
      })
    }
    for (const entry of data.archive || []) {
      candidates.push({
        id: `archive:${entry.key}`,
        source: 'archive',
        version: entry.version,
        label: `归档 ${entry.version} (${formatReleaseTime(entry.archivedAt)})`,
        key: entry.key,
      })
    }
    live.candidates = candidates
    live.selectedId = candidates[0]?.id || ''
    if (!candidates.length) {
      live.error = `没有可切换的 ${file} 候选(归档为空且近期 CI 构建都不带该渠道 yml)。`
    }
  } catch (error) {
    live.error = errorText(error)
  } finally {
    live.loading = false
  }
}

function closeYmlSwitcher(row: DesktopReleaseManifest) {
  const { [switchRowKey(row)]: _closed, ...rest } = switchStates.value
  switchStates.value = rest
}

// v-for over 0/1 items gives the expanded row a local binding for its own state.
function switcherPanelFor(row: DesktopReleaseManifest) {
  const state = switchStateOf(row)
  return state ? [state] : []
}

function switchPreviewVNode(
  target: YmlSwitchTarget,
  candidate: YmlSwitchCandidate,
  mirror: PromoteMirror,
) {
  const cell = (text: string) => h('td', { class: 'py-1 pr-4' }, text)
  const manifestMirror: ReleaseMirror = mirror === 'oss' ? 'oss' : 'r2'
  const mirrorSuffix = mirror === 'both' ? '' : ` (${mirror.toUpperCase()})`
  const rows = RELEASE_TARGETS.map((releaseTarget) => {
    const live = successfulManifests.value.find(
      (row) => row.mirror === manifestMirror && row.channel === releaseTarget.channel && row.platform === releaseTarget.platform,
    )
    const current = live?.version || '(读取失败)'
    const changed = releaseTarget.channel === target.channel && releaseTarget.platform === target.platform
    return h('tr', { class: changed ? 'font-semibold text-amber-600' : 'text-muted-foreground' }, [
      cell(releaseTarget.channelLabel),
      cell(releaseTarget.platformLabel),
      cell(current),
      cell(changed ? `${candidate.version} ←` : current),
    ])
  })
  return h('div', { class: 'space-y-3 text-sm' }, [
    h('p', {}, `来源:${candidate.label}`),
    h('p', {}, `写入镜像:${PROMOTE_MIRROR_LABELS[mirror]}`),
    h('table', { class: 'w-full text-xs' }, [
      h('thead', {}, h('tr', { class: 'border-b text-left text-muted-foreground' }, [
        h('th', { class: 'py-1 pr-4 font-medium' }, '渠道'),
        h('th', { class: 'py-1 pr-4 font-medium' }, '平台'),
        h('th', { class: 'py-1 pr-4 font-medium' }, `当前检查更新${mirrorSuffix}`),
        h('th', { class: 'py-1 pr-4 font-medium' }, `确认后检查更新${mirrorSuffix}`),
      ])),
      h('tbody', {}, rows),
    ]),
    h('p', { class: 'text-xs text-muted-foreground' },
      mirror === 'both'
        ? '同时写入 R2 + OSS;被覆盖的线上 yml 会先自动归档,可随时再切回。'
        : `只写入 ${mirror.toUpperCase()},另一个镜像保持不变(会产生镜像间版本漂移,表格里可见);被覆盖的 yml 先自动归档。`),
  ])
}

async function applyYmlSwitch(row: DesktopReleaseManifest) {
  const state = switchStateOf(row)
  if (!state || state.applying) return
  const target = state.target
  const candidate = state.candidates.find((entry) => entry.id === state.selectedId)
  if (!candidate) return
  const mirror = switchEffectiveMirror(state)
  const confirmed = await useDialog().confirm({
    title: `切换 ${target.channelLabel} / ${target.platformLabel} 到 ${candidate.version}`,
    body: switchPreviewVNode(target, candidate, mirror),
    confirmButtonText: '确认切换',
    cancelButtonText: '取消',
    confirmButtonVariant: 'warning',
  })
  if (confirmed !== 'confirm') return
  state.applying = true
  state.error = ''
  const task = startPromoterTask(`切换 ${target.channelLabel} / ${target.platformLabel} → ${candidate.version}`)
  try {
    let ossErrors: string[] = []
    if (candidate.source === 'archive') {
      const ret = await task.request<{ version: string, oss?: { errors: string[] } }>(
        `${PROMOTER_BASE}/rollback`,
        promoteHeaders(),
        { key: candidate.key, mirror },
      )
      ossErrors = ret?.oss?.errors || []
    } else {
      const ret = await task.request<{ oss?: { errors: string[] } }>(`${PROMOTER_BASE}/publish`, promoteHeaders(), {
        build: candidate.build,
        channel: workerChannelOf(target.file),
        platform: target.platform,
        mirror,
      })
      ossErrors = ret?.oss?.errors || []
    }
    if (ossErrors.length) {
      state.error = mirror === 'oss'
        ? `OSS 写入失败,请重试:${ossErrors.join('; ')}`
        : `R2 已切换,但 OSS 同步失败,请重试:${ossErrors.join('; ')}`
      task.fail(state.error)
    } else {
      task.succeed(`${PROMOTE_MIRROR_LABELS[mirror]} · ${target.file} 已切换到 ${candidate.version}`)
      closeYmlSwitcher(row)
    }
    await Promise.all([
      refreshReleaseManifests(),
      ymlHistoryLoaded.value ? loadYmlHistory() : Promise.resolve(),
      promotePlanBuild.value ? loadPromotePlan() : Promise.resolve(),
    ])
    scheduleManifestRecheck()
  } catch (error) {
    const detail = errorText(error)
    const missing = (error as any)?.data?.missing
    state.error = detail.includes('unavailable in selected mirror')
      ? `所选版本的二进制在 ${PROMOTE_MIRROR_LABELS[mirror]} 不可用。缺失:${(Array.isArray(missing) ? missing : []).join('、')}`
      : detail
    task.fail(`切换失败:${state.error}`)
  } finally {
    state.applying = false
  }
}

// yml 发布历史:每次发布/回滚覆盖 live yml 前,worker 会把旧文件快照到
// pandadl 的 yml-archive/,归档 + 当前 live 构成完整历史,可一键回滚。
type YmlArchiveEntry = {
  key: string
  file: string
  version: string
  archivedAt: string
  size: number
}

const ymlHistory = ref<YmlArchiveEntry[]>([])
const ymlHistoryLoading = ref(false)
const ymlHistoryLoaded = ref(false)
const ymlRollbackKey = ref('')

async function loadYmlHistory() {
  ymlHistoryLoading.value = true
  promoteError.value = ''
  try {
    const data = await $fetch<{ entries: YmlArchiveEntry[] }>(`${PROMOTER_BASE}/yml-archive`, {
      headers: promoteHeaders(),
    })
    ymlHistory.value = data.entries || []
    ymlHistoryLoaded.value = true
  } catch (error) {
    promoteError.value = errorText(error)
  } finally {
    ymlHistoryLoading.value = false
  }
}

async function rollbackYmlEntry(entry: YmlArchiveEntry) {
  if (ymlRollbackKey.value) return
  const confirmed = await useDialog().confirm({
    title: `回滚 ${entry.file}`,
    body: `把线上 ${entry.file} 回滚到 ${entry.version}(归档于 ${formatReleaseTime(entry.archivedAt)})。\n写入 R2 + OSS,对应渠道客户端立即生效;当前线上 yml 会先自动归档。`,
    confirmButtonText: '回滚',
    cancelButtonText: '取消',
    confirmButtonVariant: 'warning',
  })
  if (confirmed !== 'confirm') return
  ymlRollbackKey.value = entry.key
  promoteError.value = ''
  const task = startPromoterTask(`回滚 ${entry.file} → ${entry.version}`)
  try {
    const ret = await task.request<{ restored: string, version: string, oss?: { errors: string[] } }>(
      `${PROMOTER_BASE}/rollback`,
      promoteHeaders(),
      { key: entry.key },
    )
    const ossErrors = ret?.oss?.errors || []
    if (ossErrors.length) {
      promoteError.value = `R2 已回滚,但 OSS 同步失败,请重试:${ossErrors.join('; ')}`
      task.fail(promoteError.value)
    } else {
      task.succeed(`${entry.file} 已回滚到 ${ret?.version || entry.version}\n${ret?.restored || ''}`)
    }
    await Promise.all([refreshReleaseManifests(), loadYmlHistory(), promotePlanBuild.value ? loadPromotePlan() : Promise.resolve()])
    scheduleManifestRecheck()
  } catch (error) {
    promoteError.value = errorText(error)
    task.fail(`回滚失败:${promoteError.value}`)
  } finally {
    ymlRollbackKey.value = ''
  }
}

// worker 对渠道 yml 做字节级对比并解析版本号(尺寸对比对 yml 无效:
// sha512/size/版本号都是定宽,不同版本经常撞出相同字节数)。
function promoteYmlStateLabel(row: PromotePlanEntry) {
  const meta = row.meta
  if (!meta) return '-'
  if (meta.liveVersion === null) return `线上暂无 → ${meta.buildVersion}`
  if (meta.identical) return `与线上一致 (${meta.buildVersion})`
  return `线上 ${meta.liveVersion} → ${meta.buildVersion}`
}

onMounted(() => {
  refresh()
  refreshPromoteBuilds()
  manifestPollTimer = setInterval(manifestPollTick, MANIFEST_POLL_MS)
})

onUnmounted(() => {
  if (manifestPollTimer) clearInterval(manifestPollTimer)
  if (manifestRecheckTimer) clearTimeout(manifestRecheckTimer)
})
</script>

<template lang="pug">
div(class="mx-auto w-full max-w-7xl px-2 py-6")
  div(class="flex flex-col gap-3 border-b pb-5 sm:flex-row sm:items-start sm:justify-between")
    div(class="space-y-1")
      h1(class="text-2xl font-semibold tracking-tight") 桌面版本
      p(class="text-sm text-muted-foreground") 桌面客户端版本统计、R2/OSS release yml 和教程页下载版本。
    div(class="flex flex-wrap items-center gap-3")
      label(class="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground")
        Switch(v-model="autoRefreshEnabled")
        | {{ manifestsUpdatedLabel }}
      Button(variant="outline" size="sm" class="w-fit" @click="refresh")
        Icon(name="material-symbols:refresh" class="mr-2 h-4 w-4")
        | 刷新

  div(class="mt-8 grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]")
    Card(class="gap-0 py-0")
      CardHeader(class="border-b px-6 py-5")
        div(class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between")
          div
            CardTitle(class="text-base") 教程页下载版本
            p(class="mt-1 text-xs text-muted-foreground") Windows/macOS 教程页面读取这里的 stable 和 beta 版本号。
          Badge(v-if="downloadVersionLoading" variant="secondary") loading
      CardContent(class="px-6 py-6")
        div(class="grid gap-4 md:grid-cols-2")
          div(class="grid gap-1.5 text-sm")
            span Stable
            MultiSelect(
              v-model="stableVersionModel"
              :options="versionOptions"
              :multiple="false"
              :filterable="true"
              :allow-create="true"
              :prefill-search-on-open="false"
              single-selection-mode="text"
              placeholder="2.2.0"
              search-placeholder="选择或输入版本号..."
              empty-text="无匹配版本，输入后回车即可使用"
              toggle-aria-label="选择 stable 版本"
            )
          div(class="grid gap-1.5 text-sm")
            div(class="flex items-center justify-between")
              span Beta
              button(
                v-if="betaVersion"
                type="button"
                class="text-xs text-muted-foreground underline-offset-2 hover:underline"
                @click="betaVersion = ''"
              ) 留空
            MultiSelect(
              v-model="betaVersionModel"
              :options="versionOptions"
              :multiple="false"
              :filterable="true"
              :allow-create="true"
              :prefill-search-on-open="false"
              single-selection-mode="text"
              placeholder="2.3.2-beta.4 或留空"
              search-placeholder="选择或输入版本号..."
              empty-text="无匹配版本，输入后回车即可使用"
              toggle-aria-label="选择 beta 版本"
            )
        div(class="mt-5 flex flex-wrap gap-2")
          Button(
            v-if="currentStableManifest"
            size="sm"
            variant="outline"
            @click="setDraftVersion('stable', currentStableManifest.version)"
          ) 使用 {{ currentStableManifest.mirrorLabel }} stable {{ currentStableManifest.version }}
          Button(
            v-if="currentBetaManifest"
            size="sm"
            variant="outline"
            @click="setDraftVersion('beta', currentBetaManifest.version)"
          ) 使用 {{ currentBetaManifest.mirrorLabel }} beta {{ currentBetaManifest.version }}
        div(class="mt-6 flex flex-col gap-3 border-t pt-5 sm:flex-row sm:items-center sm:justify-between")
          p(class="text-xs text-muted-foreground") Beta 留空会写成 `-`，下载页不展示 Beta 按钮。
          Button(:disabled="downloadVersionSaving || !stableVersion.trim()" @click="saveDownloadVersions")
            Icon(name="material-symbols:save-outline" class="mr-2 h-4 w-4")
            | {{ downloadVersionSaving ? '保存中...' : '保存下载版本' }}

    Card(class="gap-0 py-0")
      CardHeader(class="border-b px-5 py-4")
        CardTitle(class="text-base") 当前候选
      CardContent(class="divide-y px-5 py-0")
        div(class="py-4")
          div(class="mb-2 flex items-center justify-between")
            Badge(variant="default") stable
            span(class="text-xs text-muted-foreground") 教程页
          p(class="truncate text-sm font-medium" :title="stableVersion || '-'") {{ stableVersion || '-' }}
        div(class="py-4")
          div(class="mb-2 flex items-center justify-between")
            Badge(variant="secondary") beta
            span(class="text-xs text-muted-foreground") 教程页
          p(class="truncate text-sm font-medium" :title="betaVersion || '-'") {{ betaVersion || '-' }}
        div(class="py-4")
          p(class="text-xs text-muted-foreground") 版本候选来自 R2/OSS release yml 和 {{ metricWindowSummary }} Axiom 统计,yml 每 30 秒自动刷新,发现新版本会提醒。

  Card(class="mt-8 gap-0 py-0")
    CardHeader(class="border-b px-6 py-5")
      div(class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between")
        div
          div(class="flex items-center gap-2")
            CardTitle(class="text-base") CI 构建提升
            Badge(v-if="promoteBuildsLoading || promotePlanLoading" variant="secondary") loading
          p(class="mt-1 text-xs text-muted-foreground") 把 CI 上传到 build bucket 的产物同时写入 pandadl(R2)和 OSS,再发布渠道 yml 生效更新。发布和切换前会检查所选镜像的二进制,OSS 缺失或大小不对时自动补传。
        Button(size="sm" variant="outline" class="h-8" :disabled="promoteBuildsLoading" @click="refreshPromoteBuilds")
          Icon(name="material-symbols:refresh" class="mr-1 h-4 w-4" :class="{ 'animate-spin': promoteBuildsLoading }")
          | builds
    CardContent(class="px-6 py-5")
      div(class="flex flex-col gap-3 md:flex-row md:items-center")
        select(
          v-model="promoteSelectedBuild"
          :disabled="promotePlanLoading || promoteCopying || !!promotePublishing"
          class="h-9 w-full max-w-md rounded-md border border-input bg-transparent px-3 text-sm"
        )
          option(v-if="!promoteBuilds.length" disabled value="") 暂无 CI 构建
          option(v-for="build in promoteBuilds" :key="build" :value="build") {{ build }}
        Button(size="sm" variant="outline" :disabled="!promoteSelectedBuild || promotePlanLoading || promoteCopying || !!promotePublishing" @click="loadPromotePlan")
          Icon(v-if="promotePlanLoading" name="mdi:loading" class="mr-1 h-4 w-4 animate-spin")
          Icon(v-else name="material-symbols:manage-search" class="mr-1 h-4 w-4")
          | 读取计划
      div(v-if="promoteError" class="mt-4 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive") {{ promoteError }}
      template(v-if="promotePlanBuild && promoteBinaries.length")
        div(class="mt-5 overflow-x-auto")
          table(class="w-full text-sm")
            thead
              tr(class="border-b text-left text-muted-foreground")
                th(class="py-2 pr-4 font-medium") 文件
                th(class="py-2 pr-4 font-medium") 大小
                th(class="py-2 pr-4 font-medium") 目标 (pandadl)
                th(class="py-2 pr-0 font-medium") 状态
            tbody
              tr(v-for="row in promoteBinaries" :key="row.source" class="border-b last:border-b-0")
                td(class="max-w-[300px] truncate py-2 pr-4 font-mono text-xs" :title="row.source") {{ row.source }}
                td(class="whitespace-nowrap py-2 pr-4") {{ formatBytes(row.size) }}
                td(class="max-w-[340px] truncate py-2 pr-4 font-mono text-xs" :title="row.dests.join('\\n')") {{ row.dests.join(', ') }}
                td(class="whitespace-nowrap py-2 pr-0")
                  Badge(
                    :variant="promoteEntryState(row) === 'error' ? 'destructive' : promoteEntryState(row).endsWith('pending') ? 'outline' : promoteEntryState(row) === 'copying' ? 'secondary' : 'default'"
                  )
                    Icon(v-if="promoteEntryState(row) === 'copying'" name="mdi:loading" class="mr-1 h-3 w-3 animate-spin")
                    | {{ PROMOTE_STATE_LABELS[promoteEntryState(row)] }}
        AdminDesktopReleaseNotesEditor(v-if="promoteNotesVersion" :key="promoteNotesVersion" ref="promoteNotesEditor" :version="promoteNotesVersion" @saved="loadVersionNotesHistory" class="mt-5 rounded-md border p-4")
        p(v-else class="mt-4 text-sm text-destructive") 无法确定此构建的唯一版本号，暂不能发布。
        div(v-if="promoteMetadata.length" class="mt-4 rounded-md border bg-muted/30 px-3 py-2.5")
          p(class="mb-1.5 text-xs font-medium text-muted-foreground") 渠道 yml(点「发布」时才会写入 R2 + OSS,写入即生效更新)
          div(class="flex flex-wrap gap-x-5 gap-y-1")
            div(v-for="row in promoteMetadata" :key="row.source" class="flex items-center gap-1.5 font-mono text-xs")
              span {{ row.source }}
              Badge(variant="outline" class="font-sans text-[10px]") {{ promoteYmlStateLabel(row) }}
        div(class="mt-4 flex flex-wrap items-center gap-3 border-t pt-4")
          label(class="flex items-center gap-2 text-sm")
            Checkbox#v2panda-promote-force(v-model="promoteForce")
            span 覆盖已同步文件
          label(class="flex items-center gap-2 text-sm")
            span(class="text-xs text-muted-foreground") 发布镜像
            select(
              v-model="promotePublishMirror"
              class="h-8 rounded-md border border-input bg-transparent px-2 text-sm"
            )
              option(value="both") R2 + OSS
              option(value="r2") 仅 R2
              option(value="oss") 仅 OSS
          Button(:disabled="promoteCopying || (!promoteForce && !promotePending.length)" @click="runPromoteCopy")
            Icon(name="material-symbols:cloud-upload" class="mr-2 h-4 w-4")
            | {{ promoteCopying ? '上传中...' : promotePending.length ? `上传产物 (${promotePending.length})` : promoteForce ? `重传全部 (${promoteBinaries.length})` : '产物已同步' }}
          template(v-for="ch in promoteChannels" :key="ch.channel")
            div(class="flex items-center gap-1.5 rounded-md border px-2 py-1")
              span(class="px-1 text-xs font-medium") {{ ch.channel }}
              Button(
                size="sm"
                variant="secondary"
                class="h-7 px-2"
                :disabled="!ch.mac || !ch.win || !promoteReady || !!promotePublishing"
                :title="promoteReady ? '发布 mac + win 渠道 yml' : '先上传全部产物'"
                @click="publishPromoteChannel(ch.channel, 'both')"
              )
                Icon(v-if="promotePublishing === ch.channel + ':both'" name="mdi:loading" class="mr-1 h-3.5 w-3.5 animate-spin")
                Icon(v-else name="material-symbols:rocket-launch-outline" class="mr-1 h-3.5 w-3.5")
                | 全部
              Button(
                size="sm"
                variant="outline"
                class="h-7 px-2"
                :disabled="!ch.mac || !promoteReadyMac || !!promotePublishing"
                :title="!ch.mac ? '此 build 没有 mac 渠道 yml' : promoteReadyMac ? '只发布 ' + ch.channel + '-mac.yml' : '先上传 macOS 产物'"
                @click="publishPromoteChannel(ch.channel, 'mac')"
              )
                Icon(v-if="promotePublishing === ch.channel + ':mac'" name="mdi:loading" class="mr-1 h-3.5 w-3.5 animate-spin")
                | macOS
              Button(
                size="sm"
                variant="outline"
                class="h-7 px-2"
                :disabled="!ch.win || !promoteReadyWin || !!promotePublishing"
                :title="!ch.win ? '此 build 没有 win 渠道 yml' : promoteReadyWin ? '只发布 ' + ch.channel + '.yml' : '先上传 Windows 产物'"
                @click="publishPromoteChannel(ch.channel, 'win')"
              )
                Icon(v-if="promotePublishing === ch.channel + ':win'" name="mdi:loading" class="mr-1 h-3.5 w-3.5 animate-spin")
                | Windows
      div(class="mt-5 border-t pt-4")
        div(class="flex items-center justify-between gap-3")
          p(class="text-xs font-medium text-muted-foreground") 渠道 yml 发布历史(发布/回滚覆盖 live yml 前自动归档旧文件,可一键回滚)
          Button(size="sm" variant="outline" class="h-7" :disabled="ymlHistoryLoading" @click="loadYmlHistory")
            Icon(name="material-symbols:history" class="mr-1 h-3.5 w-3.5" :class="{ 'animate-spin': ymlHistoryLoading }")
            | {{ ymlHistoryLoaded ? '刷新历史' : '加载历史' }}
        div(v-if="ymlHistoryLoaded && !ymlHistory.length" class="mt-3 text-xs text-muted-foreground") 暂无归档——第一次通过本页发布并覆盖旧 yml 时会自动生成。
        div(v-if="ymlHistory.length" class="mt-3 overflow-x-auto")
          table(class="w-full text-sm")
            thead
              tr(class="border-b text-left text-muted-foreground")
                th(class="py-2 pr-4 font-medium") 文件
                th(class="py-2 pr-4 font-medium") 版本
                th(class="py-2 pr-4 font-medium") 归档时间
                th(class="py-2 pr-0 text-right font-medium") 操作
            tbody
              tr(v-for="entry in ymlHistory" :key="entry.key" class="border-b last:border-b-0")
                td(class="py-2 pr-4 font-mono text-xs") {{ entry.file }}
                td(class="py-2 pr-4 font-medium") {{ entry.version }}
                td(class="whitespace-nowrap py-2 pr-4") {{ formatReleaseTime(entry.archivedAt) }}
                td(class="py-2 pr-0 text-right")
                  Button(size="sm" variant="ghost" class="mr-2 h-7" @click="editReleaseNotes(entry.version)") 编辑更新说明
                  Button(size="sm" variant="outline" class="h-7" :disabled="!!ymlRollbackKey" @click="rollbackYmlEntry(entry)")
                    Icon(v-if="ymlRollbackKey === entry.key" name="mdi:loading" class="mr-1 h-3.5 w-3.5 animate-spin")
                    Icon(v-else name="material-symbols:undo" class="mr-1 h-3.5 w-3.5")
                    | 回滚

  Card(class="mt-8 gap-0 py-0")
    CardHeader(class="border-b px-6 py-5")
      div(class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between")
        div
          div(class="flex items-center gap-2")
            CardTitle(class="text-base") R2 / OSS Release YML
            Badge(v-if="manifestLoading" variant="secondary") loading
          p(class="mt-1 text-xs text-muted-foreground") 路径来自 V2Panda upload/release 脚本：`panda/dl/client/{mac,win}`。
        Button(size="sm" variant="outline" class="h-8" :disabled="manifestLoading" @click="refreshReleaseManifests")
          Icon(name="material-symbols:refresh" class="mr-1 h-4 w-4" :class="{ 'animate-spin': manifestLoading }")
          | yml
    CardContent(class="px-6 py-3")
      div(v-if="manifestError" class="border-b py-3 text-sm text-destructive") {{ manifestError }}
      div(class="overflow-x-auto")
        table(class="w-full text-sm")
          thead
            tr(class="border-b text-left text-muted-foreground")
              th(class="py-3 pr-4 font-medium") 源
              th(class="py-3 pr-4 font-medium") 渠道
              th(class="py-3 pr-4 font-medium") 平台
              th(class="py-3 pr-4 font-medium") 版本
              th(class="py-3 pr-4 font-medium") 上传时间
              th(class="py-3 pr-4 font-medium") path
              th(class="py-3 pr-0 text-right font-medium") 操作
          tbody
            tr(v-if="manifestLoading && !releaseManifests.length")
              td(class="py-10 text-center text-muted-foreground" colspan="7")
                div(class="inline-flex items-center gap-2")
                  Icon(name="mdi:loading" class="h-4 w-4 animate-spin")
                  span 正在加载 release yml...
            template(v-for="row in releaseManifests" :key="`${row.mirror}-${row.manifestPath}`")
              tr(class="border-b")
                td(class="py-3 pr-4")
                  Badge(:variant="row.mirror === 'r2' ? 'default' : 'outline'") {{ row.mirrorLabel }}
                td(class="py-3 pr-4") {{ row.channelLabel }}
                td(class="py-3 pr-4") {{ row.platformLabel }}
                td(class="py-3 pr-4 font-medium")
                  span(v-if="row.ok") {{ row.version }}
                  span(v-else class="text-destructive") 读取失败
                td(class="py-3 pr-4 whitespace-nowrap") {{ row.ok ? formatReleaseTime(row.releaseDate) : '-' }}
                td(class="max-w-[280px] py-3 pr-4 font-mono text-xs")
                  template(v-if="row.ok")
                    div(v-for="link in releaseFileLinks(row)" :key="link.name" class="flex items-center gap-1.5")
                      a(
                        class="truncate underline-offset-2 hover:underline"
                        :title="link.href"
                        :href="link.href"
                        target="_blank"
                        rel="noopener noreferrer"
                      ) {{ link.name }}
                      button(
                        type="button"
                        class="shrink-0 text-muted-foreground transition-colors hover:text-foreground"
                        title="复制链接"
                        @click="$utils.copy(link.href)"
                      )
                        Icon(name="material-symbols:content-copy-outline" class="h-3.5 w-3.5")
                  span(v-else :title="row.error") {{ row.error }}
                td(class="py-3 pr-0")
                  div(class="flex justify-end gap-2")
                    template(v-if="row.ok")
                      Button(size="sm" variant="ghost" @click="editReleaseNotes(row.version)") 更新说明
                      Button(size="sm" variant="ghost" @click="setDraftVersion('stable', row.version)") Stable
                      Button(size="sm" variant="ghost" @click="setDraftVersion('beta', row.version)") Beta
                    Button(
                      size="sm"
                      variant="outline"
                      class="h-8"
                      title="切换该渠道/平台的线上 yml 到任意历史版本或 CI 构建"
                      @click="openYmlSwitcher(row)"
                    )
                      Icon(name="material-symbols:swap-horiz" class="mr-1 h-4 w-4")
                      | 切换
              tr(
                v-for="state in switcherPanelFor(row)"
                :key="`${row.mirror}-${state.target.file}-switcher`"
                class="border-b bg-muted/20"
              )
                td(colspan="7" class="px-3 py-4")
                  div(class="flex flex-col gap-3 md:flex-row md:items-center")
                    p(class="text-sm font-medium") 切换 {{ state.target.channelLabel }} / {{ state.target.platformLabel }}
                      span(class="ml-2 font-mono text-xs text-muted-foreground") {{ state.target.file }}
                    div(v-if="state.loading" class="inline-flex items-center gap-2 text-sm text-muted-foreground")
                      Icon(name="mdi:loading" class="h-4 w-4 animate-spin")
                      span 正在加载候选版本...
                    template(v-else-if="state.candidates.length")
                      select(
                        v-model="state.selectedId"
                        class="h-9 w-full max-w-md rounded-md border border-input bg-transparent px-3 text-sm"
                      )
                        option(v-for="candidate in state.candidates" :key="candidate.id" :value="candidate.id") {{ candidate.label }}
                      label(class="flex items-center gap-2 whitespace-nowrap text-sm")
                        Checkbox(:id="'v2panda-switch-both-' + row.mirror + '-' + state.target.file" v-model="state.bothMirrors")
                        span 同写 R2 + OSS
                      Badge(v-if="!state.bothMirrors" variant="outline") 仅 {{ state.target.mirrorLabel }}
                      Button(size="sm" :disabled="!state.selectedId || state.applying" @click="applyYmlSwitch(row)")
                        Icon(v-if="state.applying" name="mdi:loading" class="mr-1 h-4 w-4 animate-spin")
                        Icon(v-else name="material-symbols:swap-horiz" class="mr-1 h-4 w-4")
                        | 预览并切换
                    Button(size="sm" variant="ghost" :disabled="state.applying" @click="closeYmlSwitcher(row)") 收起
                  div(v-if="state.error" class="mt-3 text-sm text-destructive") {{ state.error }}
            tr(v-if="!manifestLoading && !releaseManifests.length")
              td(class="py-10 text-center text-muted-foreground" colspan="7") 暂无 release yml 数据

  Card(class="mt-8 gap-0 py-0")
    CardHeader(class="border-b px-6 py-5")
      div(class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between")
        div
          div(class="flex items-center gap-2")
            CardTitle(class="text-base") Axiom 版本统计
            Badge(v-if="metricsLoading" variant="secondary") loading
          p(class="mt-1 text-xs text-muted-foreground") {{ metricWindowSummary }}，{{ mergePlatforms ? '按版本合并 macOS/Windows' : '按版本和平台' }} 统计心跳、启动、流量。
        div(class="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-end")
          div(class="flex h-8 items-center gap-2 rounded-md border px-3 text-sm")
            Checkbox#v2panda-merge-platforms(v-model="mergePlatforms")
            label(class="cursor-pointer whitespace-nowrap" for="v2panda-merge-platforms") 合并平台
          div(class="flex flex-col gap-2 sm:items-end")
            div(class="inline-flex flex-wrap overflow-hidden rounded-md border bg-background")
              Button(
                v-for="option in metricWindowOptions"
                :key="option.key"
                size="sm"
                :variant="selectedMetricWindowKey === option.key ? 'secondary' : 'ghost'"
                class="h-8 rounded-none border-0 px-3"
                :disabled="metricsLoading"
                @click="setMetricWindow(option.key)"
              ) {{ option.label }}
              Button(
                size="sm"
                :variant="isCustomMetricWindow ? 'secondary' : 'ghost'"
                class="h-8 rounded-none border-0 px-3"
                :disabled="metricsLoading"
                @click="setMetricWindow('custom')"
              ) 自定义
            div(v-if="isCustomMetricWindow" class="flex flex-col gap-2 sm:flex-row sm:items-center")
              Input(
                type="datetime-local"
                v-model="customMetricRange[0]"
                class="h-8 sm:w-[180px]"
                :disabled="metricsLoading"
              )
              span(class="hidden text-xs text-muted-foreground sm:inline") ~
              Input(
                type="datetime-local"
                v-model="customMetricRange[1]"
                class="h-8 sm:w-[180px]"
                :disabled="metricsLoading"
              )
              Button(size="sm" variant="outline" class="h-8" :disabled="metricsLoading" @click="applyCustomMetricRange") 应用
          Button(size="sm" variant="outline" class="h-8" :disabled="metricsLoading" @click="refreshMetrics")
            Icon(name="material-symbols:refresh" class="mr-1 h-4 w-4" :class="{ 'animate-spin': metricsLoading }")
            | 指标
    CardContent(class="px-6 py-3")
      div(v-if="metricsError" class="border-b py-3 text-sm text-destructive") {{ metricsError }}
      div(class="overflow-x-auto")
        table(class="w-full text-sm")
          thead
            tr(class="border-b text-left text-muted-foreground")
              th(class="py-3 pr-4 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('version')")
                  span 版本
                  Icon(:name="metricSortIcon('version')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'version' }")
              th(v-show="!mergePlatforms" class="py-3 pr-4 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('platform')")
                  span 平台
                  Icon(:name="metricSortIcon('platform')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'platform' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('installCount')")
                  span 安装设备
                  Icon(:name="metricSortIcon('installCount')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'installCount' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('users')")
                  span 用户
                  Icon(:name="metricSortIcon('users')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'users' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('heartbeatCount')")
                  span 心跳
                  Icon(:name="metricSortIcon('heartbeatCount')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'heartbeatCount' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('launchCount')")
                  span 服务启动
                  Icon(:name="metricSortIcon('launchCount')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'launchCount' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('usageEventCount')")
                  span 流量事件
                  Icon(:name="metricSortIcon('usageEventCount')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'usageEventCount' }")
              th(class="py-3 pr-4 text-right font-medium")
                button(type="button" class="ml-auto inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('trafficBytes')")
                  span 流量总量
                  Icon(:name="metricSortIcon('trafficBytes')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'trafficBytes' }")
              th(class="py-3 pr-4 font-medium")
                button(type="button" class="inline-flex items-center gap-1 hover:text-foreground" @click="toggleMetricSort('lastSeenAt')")
                  span 最新上报
                  Icon(:name="metricSortIcon('lastSeenAt')" class="h-3.5 w-3.5" :class="{ 'opacity-40': metricSort.key !== 'lastSeenAt' }")
          tbody
            tr(v-if="metricsLoading && !metricRows.length")
              td(class="py-10 text-center text-muted-foreground" :colspan="mergePlatforms ? 8 : 9")
                div(class="inline-flex items-center gap-2")
                  Icon(name="mdi:loading" class="h-4 w-4 animate-spin")
                  span 正在加载 Axiom 统计...
            tr(v-for="row in metricRows" :key="`${row.version}-${row.platform}`" class="border-b last:border-b-0")
              td(class="py-3 pr-4 font-medium") {{ row.version }}
              td(v-show="!mergePlatforms" class="py-3 pr-4") {{ metricPlatformLabel(row.platform) }}
              td(class="py-3 pr-4 text-right tabular-nums") {{ formatMetricNumber(row.installCount) }}
              td(class="py-3 pr-4 text-right tabular-nums")
                span(class="inline-flex items-center justify-end gap-1.5 leading-none")
                  span {{ formatMetricNumber(metricUserCount(row)) }}
                  button(
                    type="button"
                    class="inline-flex items-center text-muted-foreground hover:text-foreground"
                    title="查看用户明细"
                    aria-label="查看用户明细"
                    @click="openVersionUsers(row)"
                  )
                    Icon(name="material-symbols:search" class="block h-4 w-4")
              td(class="py-3 pr-4 text-right tabular-nums") {{ formatMetricNumber(row.heartbeatCount) }}
              td(class="py-3 pr-4 text-right tabular-nums") {{ formatMetricNumber(row.launchCount) }}
              td(class="py-3 pr-4 text-right tabular-nums") {{ formatMetricNumber(row.usageEventCount) }}
              td(class="py-3 pr-4 text-right tabular-nums" :title="trafficMetricTitle(row)") {{ formatBytes(row.trafficBytes) }}
              td(class="py-3 pr-4 whitespace-nowrap") {{ formatMetricTime(row.lastSeenAt) }}
            tr(v-if="!metricsLoading && !metricRows.length")
              td(class="py-10 text-center text-muted-foreground" :colspan="mergePlatforms ? 8 : 9") 暂无 Axiom 统计数据

  Card(class="mt-8 gap-0 py-0")
    CardHeader(class="border-b px-6 py-5")
      div(class="flex items-center justify-between gap-3")
        CardTitle(class="text-base") 历史版本更新说明
        Button(size="sm" variant="outline" :disabled="versionNotesHistoryLoading" @click="loadVersionNotesHistory") 刷新
    CardContent(class="px-6 py-3")
      p(v-if="versionNotesHistoryError" class="py-3 text-sm text-destructive" role="alert") {{ versionNotesHistoryError }}
      p(v-else-if="versionNotesHistoryLoading" class="py-3 text-sm text-muted-foreground") 正在加载历史版本...
      p(v-else-if="!versionNotesHistory.length" class="py-3 text-sm text-muted-foreground") 暂无已登记的桌面版本
      div(v-else class="overflow-x-auto")
        table(class="w-full text-sm")
          thead
            tr(class="border-b text-left text-muted-foreground")
              th(class="py-2 pr-4 font-medium") 版本
              th(class="py-2 pr-4 font-medium") 发布时间 (UTC{{ dayjs().format('Z') }})
              th(class="py-2 pr-4 font-medium") 中文
              th(class="py-2 pr-4 font-medium") English
              th(class="py-2 text-right font-medium") 操作
          tbody
            tr(v-for="entry in versionNotesHistory" :key="entry.version" class="border-b last:border-b-0")
              td(class="py-3 pr-4 font-medium") {{ entry.version }}
              td(class="whitespace-nowrap py-3 pr-4") {{ formatReleaseTime(entry.publishedAt || undefined) }}
              td(class="max-w-xs py-3 pr-4")
                p(class="line-clamp-3 whitespace-pre-wrap break-words") {{ entry.changelog || '—' }}
              td(class="max-w-xs py-3 pr-4")
                p(class="line-clamp-3 whitespace-pre-wrap break-words") {{ entry.changelog_en || '—' }}
              td(class="py-3 text-right")
                Button(size="sm" variant="outline" @click="editReleaseNotes(entry.version)") 编辑更新说明

  Sheet(v-model:open="notesSheetOpen")
    SheetContent(side="right" class="w-full overflow-y-auto sm:max-w-2xl")
      SheetHeader
        SheetTitle 编辑更新说明
        SheetDescription 修改 {{ editingNotesVersion }} 的更新说明，保存后客户端下次检查更新时可见。
      AdminDesktopReleaseNotesEditor(v-if="notesSheetOpen" :key="editingNotesVersion" :version="editingNotesVersion" @saved="loadVersionNotesHistory" class="mt-6")

  Sheet(v-model:open="userSheetOpen")
    SheetContent(side="right" class="flex w-full flex-col gap-0 p-0 sm:max-w-4xl")
      SheetHeader(class="border-b px-5 py-4 pr-12 text-left")
        SheetTitle 用户明细 · {{ userSheetTitle }}
        SheetDescription {{ metricWindowSummary }}，按流量总量排序，最多 500 个用户。
      div(class="flex-1 overflow-auto px-5 py-4")
        div(v-if="userSheetError" class="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive") {{ userSheetError }}
        div(v-else-if="userSheetLoading" class="flex items-center justify-center gap-2 py-16 text-sm text-muted-foreground")
          Icon(name="mdi:loading" class="h-4 w-4 animate-spin")
          span 正在加载用户明细...
        div(v-else-if="!userSheetRows.length" class="py-16 text-center text-sm text-muted-foreground") 该版本在此时间范围内没有用户数据
        table(v-else class="w-full text-sm")
          thead
            tr(class="border-b text-left text-muted-foreground")
              th(class="py-2 pr-4 font-medium") 用户
              th(class="py-2 pr-4 text-right font-medium") 设备
              th(class="py-2 pr-4 font-medium") 系统版本
              th(class="py-2 pr-4 text-right font-medium") 心跳
              th(class="py-2 pr-4 text-right font-medium") 服务启动
              th(class="py-2 pr-4 text-right font-medium") 流量事件
              th(class="py-2 pr-0 text-right font-medium") 流量总量
          tbody
            tr(v-for="user in userSheetRows" :key="user.userId" class="border-b last:border-b-0")
              td(class="py-2 pr-4")
                NuxtLink(
                  class="text-blue-600 hover:underline"
                  :to="`/dashboard/${user.userId}`"
                  target="_blank"
                ) {{ user.userId }}
              td(class="py-2 pr-4 text-right tabular-nums") {{ formatMetricNumber(user.deviceCount) }}
              td(class="max-w-[220px] truncate py-2 pr-4" :title="user.osVersions.join(', ') || '-'") {{ user.osVersions.join(', ') || '-' }}
              td(class="py-2 pr-4 text-right tabular-nums") {{ formatMetricNumber(user.heartbeatCount) }}
              td(class="py-2 pr-4 text-right tabular-nums") {{ formatMetricNumber(user.launchCount) }}
              td(class="py-2 pr-4 text-right tabular-nums") {{ formatMetricNumber(user.usageEventCount) }}
              td(class="py-2 pr-0 text-right tabular-nums" :title="trafficMetricTitle(user)") {{ formatBytes(user.trafficBytes) }}
  PromoterProgressPanel
</template>
